import { useState, useRef, useEffect } from "react";
import { playNow, kickAudio, getPiano } from "@/audio/piano";

/**
 * RhythmPatternPlayer — playable rhythm patterns for the percussion lessons.
 * Used in lessons 17, 18 (Salsa Rhythm Patterns) and 33, 34 (Meters & Beats).
 *
 * v2 — adds:
 *   • Metronome click (every quarter note) that loops alongside the pattern,
 *     so the body locks into the pulse the way it does on a real instrument
 *   • True forever-loop with adjustable BPM slider
 *   • Bar counter showing where in the loop we are
 *   • 16-cell or 32-cell patterns (1 or 2 bars) auto-detected
 *
 * A pattern is a grid of cells — 16th notes by default, or triplet eighths
 * when `subdivide: 3` (needed for real swing, which a 16th grid cannot place).
 * Each cell has an optional "K" (kick / low) or "S" (snare / high) accent.
 */

export interface RhythmPattern {
  name: string;
  description: string;
  /** One bar of 4/4 is 4 × subdivide cells (16 by default). Two bars = twice that
   *  (used by son/rumba/bossa clave). */
  cells: ("K" | "S" | "")[];
  /** Cells per beat: 4 = sixteenths (default), 3 = triplet eighths, 2 = eighths. */
  subdivide?: 2 | 3 | 4;
  /** Beats per bar (default 4). 3 for a waltz; 2 for 6/8 counted in dotted quarters. */
  beats?: number;
  /** Beats per minute for this pattern (default 100). */
  bpm?: number;
}

const KICK_PITCH = "C2";       // low boom
const SNARE_PITCH = "E4";      // crisp snare
const METRO_DOWNBEAT = "C5";   // bell-like, on beat 1
const METRO_UPBEAT = "G4";     // softer, on beats 2-3-4

interface RhythmPatternPlayerProps {
  patterns: RhythmPattern[];
  /** Initially selected pattern index. */
  initial?: number;
}

export function RhythmPatternPlayer({ patterns, initial = 0 }: RhythmPatternPlayerProps) {
  const [active, setActive] = useState(initial);
  const [playing, setPlaying] = useState(false);
  const [step, setStep] = useState(-1);
  const [metronome, setMetronome] = useState(true);
  const [bpmOverride, setBpmOverride] = useState<number | null>(null);

  const intervalRef = useRef<number | null>(null);
  const stepRef = useRef(0);
  /** Bumped by every start and stop, so a start still waiting for the samples
   *  knows it was cancelled. */
  const runRef = useRef(0);

  // Always-current refs so the running tick callback sees latest state without
  // restarting the timer on every render.
  const patternRef = useRef(patterns[active]);
  const metronomeRef = useRef(metronome);
  useEffect(() => { patternRef.current = patterns[active]; }, [patterns, active]);
  useEffect(() => { metronomeRef.current = metronome; }, [metronome]);

  const currentPattern = patterns[active];
  const effectiveBpm = bpmOverride ?? currentPattern.bpm ?? 100;
  const totalCells = currentPattern.cells.length;
  const sub = currentPattern.subdivide ?? 4;   // cells per beat
  const perBar = (currentPattern.beats ?? 4) * sub; // cells per bar
  // Every pattern must be whole bars, or the loop lands on the wrong beat.
  useEffect(() => {
    for (const p of patterns) {
      const bar = (p.beats ?? 4) * (p.subdivide ?? 4);
      if (p.cells.length % bar !== 0) {
        console.error(`[RhythmPatternPlayer] "${p.name}": ${p.cells.length} cells is not a whole number of ${bar}-cell bars`);
      }
    }
  }, [patterns]);

  const stop = () => {
    runRef.current++;
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPlaying(false);
    setStep(-1);
    stepRef.current = 0;
  };

  const start = async () => {
    if (playing) { stop(); return; }
    kickAudio();
    setPlaying(true);
    stepRef.current = 0;
    setStep(0);
    // Wait for the piano before the clock starts. Ticking while the samples were
    // still downloading queued every hit, and they all fired at once as one
    // cluster the moment the samples arrived.
    const run = ++runRef.current;
    try { await getPiano(); } catch { /* playNow will retry the load */ }
    if (run !== runRef.current) return; // stopped while the samples loaded

    const stepMs = (60_000 / effectiveBpm) / sub; // one cell of this pattern's grid

    const tick = () => {
      const p = patternRef.current;
      const len = p.cells.length;
      const ps = p.subdivide ?? 4;
      const s = stepRef.current % len;

      // 1. Pattern hit
      const cell = p.cells[s];
      if (cell === "K") void playNow(KICK_PITCH, "16n", 0.85);
      else if (cell === "S") void playNow(SNARE_PITCH, "16n", 0.6);

      // 2. Metronome click on every quarter note
      if (metronomeRef.current && s % ps === 0) {
        const beatInBar = Math.floor((s % ((p.beats ?? 4) * ps)) / ps);
        const click = beatInBar === 0 ? METRO_DOWNBEAT : METRO_UPBEAT;
        void playNow(click, "32n", beatInBar === 0 ? 0.45 : 0.25);
      }

      setStep(s);
      stepRef.current = s + 1;
    };

    tick();
    intervalRef.current = window.setInterval(tick, stepMs);
  };

  // Restart timer cleanly when BPM changes during playback (so tempo updates apply)
  useEffect(() => {
    if (playing) {
      // restart with new tempo at current step position
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
      }
      const stepMs = (60_000 / effectiveBpm) / sub;
      const tick = () => {
        const p = patternRef.current;
        const len = p.cells.length;
        const ps = p.subdivide ?? 4;
        const s = stepRef.current % len;
        const cell = p.cells[s];
        if (cell === "K") void playNow(KICK_PITCH, "16n", 0.85);
        else if (cell === "S") void playNow(SNARE_PITCH, "16n", 0.6);
        if (metronomeRef.current && s % ps === 0) {
          const beatInBar = Math.floor((s % ((p.beats ?? 4) * ps)) / ps);
          const click = beatInBar === 0 ? METRO_DOWNBEAT : METRO_UPBEAT;
          void playNow(click, "32n", beatInBar === 0 ? 0.45 : 0.25);
        }
        setStep(s);
        stepRef.current = s + 1;
      };
      intervalRef.current = window.setInterval(tick, stepMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBpm, sub, perBar]);

  // Reset step when switching patterns mid-playback (keep playing, fresh start)
  useEffect(() => {
    if (playing) {
      stepRef.current = 0;
      setStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => () => {
    if (intervalRef.current != null) clearInterval(intervalRef.current);
  }, []);

  // Display: split into bars of one 4/4 bar each
  const bars = [];
  for (let i = 0; i < totalCells; i += perBar) {
    bars.push(currentPattern.cells.slice(i, i + perBar));
  }

  const currentBar = step >= 0 ? Math.floor(step / perBar) + 1 : 0;
  const currentBeat = step >= 0 ? Math.floor((step % perBar) / sub) + 1 : 0;

  return (
    <div className="my-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] to-zinc-900/[0.04] p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">
            Rhythm pattern player
          </div>
          <div className="mt-1 font-mt text-xl text-nsm-ink">
            <span className="font-semibold text-amber-300">{currentPattern.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMetronome(m => !m)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
              metronome
                ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                : "border-zinc-700 bg-zinc-900/40 text-zinc-500"
            }`}
            title="Toggle metronome click on quarter notes"
          >
            <span className={metronome ? "animate-pulse" : ""}>♩</span> Metro
          </button>
          <button
            onClick={start}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-bold text-zinc-950 transition hover:bg-amber-400"
          >
            {playing ? "■ Stop" : "▶ Play"}
          </button>
        </div>
      </div>

      {patterns.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {patterns.map((p, i) => (
            <button
              key={p.name}
              onClick={() => setActive(i)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${
                i === active ? "bg-amber-500 text-zinc-950" : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-nsm-border bg-zinc-900/50 p-3 space-y-2">
        {bars.map((bar, barIndex) => (
          <div key={barIndex} className="grid gap-1" style={{ gridTemplateColumns: `repeat(${perBar}, minmax(0, 1fr))` }}>
            {bar.map((cell, i) => {
              const absoluteI = barIndex * perBar + i;
              const isBeatStart = i % sub === 0;
              const isStep = step === absoluteI;
              return (
                <div
                  key={i}
                  className={`relative flex h-12 items-center justify-center rounded-md border text-[11px] font-bold transition-all ${
                    isBeatStart ? "border-l-2 border-l-amber-500/50" : "border-zinc-800"
                  } ${
                    cell === "K" ? "bg-amber-500/40 text-amber-100"
                    : cell === "S" ? "bg-rose-500/40 text-rose-100"
                    : "bg-zinc-800/30 text-zinc-700"
                  } ${isStep ? "ring-2 ring-amber-300 scale-110 z-10" : ""}`}
                  title={`Bar ${barIndex + 1} · Step ${i + 1}`}
                >
                  {cell || "·"}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Tempo + bar counter */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-nsm-dim">
        <label className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider">Tempo</span>
          <input
            type="range"
            min="40" max="200" step="2"
            value={effectiveBpm}
            onChange={(e) => setBpmOverride(parseInt(e.target.value))}
            className="w-32 accent-amber-500"
          />
          <span className="w-12 font-mono text-amber-300">{effectiveBpm} bpm</span>
        </label>
        {playing && (
          <span className="rounded bg-amber-500/15 px-2 py-0.5 font-mono text-amber-200">
            Bar {currentBar} · Beat {currentBeat}
          </span>
        )}
        <span className="ml-auto text-[10px] uppercase tracking-wider">
          {bars.length} bar{bars.length > 1 ? "s" : ""} · {sub === 3 ? (currentPattern.beats === 2 ? "8ths in threes" : "triplets") : sub === 2 ? "8ths" : "16ths"} · loops forever
        </span>
      </div>

      <div className="mt-2 text-[12px] text-nsm-dim">{currentPattern.description}</div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-nsm-dim">
        <span><span className="mr-1 inline-block h-3 w-3 rounded-sm bg-amber-500/60 align-middle" /> kick (low)</span>
        <span><span className="mr-1 inline-block h-3 w-3 rounded-sm bg-rose-500/60 align-middle" /> snare (high)</span>
        {metronome && <span className="text-amber-300">♩ metronome on every quarter</span>}
      </div>
    </div>
  );
}

/* ---------- preset patterns we ship with ----------
   Unless a pattern sets `subdivide` / `beats`, it is 16 cells (1 bar) or 32 cells
   (2 bars) of 16th notes in 4/4.
   Cell index map: 1 e & a 2 e & a 3 e & a 4 e & a (×2 for 2-bar patterns).
*/

/** Tresillo — 3+3+2 grouping inside one bar. Hits on 1, &-of-2, beat 4. */
export const TRESILLO: RhythmPattern = {
  name: "Tresillo",
  description: "The 3+3+2 Cuban rhythm. Hits on beat 1, the &-of-2, and beat 4 — three pulses, six+six+four 16ths, the groove at the heart of modern pop, reggaeton and Drake-era hip-hop.",
  cells: ["K","","","","","","K","","","","","","K","","",""],
  bpm: 100,
};

/** Son clave (3-2) — 2-bar pattern. Bar 1: 3 hits like tresillo. Bar 2: 2 hits on 2 and 3. */
export const SON_CLAVE: RhythmPattern = {
  name: "Son Clave (3-2)",
  description: "The famous Cuban son clave. Bar 1 = 3 strokes (1, &-of-2, 4). Bar 2 = 2 strokes (2, 3). Backbone of son, salsa, mambo.",
  cells: [
    "K","","","","","","K","","","","","","K","","","",
    "","","","","K","","","","K","","","","","","","",
  ],
  bpm: 100,
};

/** Rumba clave (3-2) — like son but the 3rd stroke moves to &-of-4 instead of beat 4. */
export const RUMBA_CLAVE: RhythmPattern = {
  name: "Rumba Clave (3-2)",
  description: "Like son clave but the 3rd stroke is pushed late — &-of-4 instead of beat 4. Used in Afro-Cuban rumba and Latin jazz.",
  cells: [
    "K","","","","","","K","","","","","","","","K","",
    "","","","","K","","","","K","","","","","","","",
  ],
  bpm: 100,
};

/**
 * Bossa nova clave — Brazilian variation, asymmetric 2-bar feel.
 *
 * Bar 2's second stroke sits on the &-of-3 (cell 26), giving the eighth-note
 * gap pattern 3-3-4-3-3. It previously sat on the "e" of 4 (cell 29), an ODD
 * sixteenth — which produced gaps of 4½ and 1½ eighths. No standard clave
 * stroke falls off the eighth-note grid; son is 3-3-4-2-4 and rumba 3-4-3-2-4,
 * so all three are son with at most one stroke pushed a single eighth later.
 */
export const BOSSA_NOVA: RhythmPattern = {
  name: "Bossa Nova Clave",
  description: "Brazilian variation. Bar 1 = 1, &-of-2, 4 (the tresillo, same as son). Bar 2 = 2, &-of-3. Gentler than son or rumba; the secret pulse under João Gilberto and Jobim.",
  cells: [
    "K","","","","","","K","","","","","","K","","","",
    "","","","","K","","","","","","K","","","","","",
  ],
  bpm: 96,
};

/** Quarter notes only — for lesson 34 "container is fixed" demo */
export const QUARTERS: RhythmPattern = {
  name: "Quarters · one event per beat",
  description: "The plainest possible bar — one hit per beat, four beats in the bar. Body nods exactly with what you hear.",
  bpm: 90,
  cells: ["K","","","","S","","","","K","","","","S","","",""],
};

/** Eighth notes — divide-by-2 demo */
export const DIV_BY_2: RhythmPattern = {
  name: "Divide by 2 · 1 and 2 and",
  description: "Each beat split in half. Downbeats on numbers, off-beats on 'and'. The eighth-note feel.",
  bpm: 90,
  cells: ["K","","S","","K","","S","","K","","S","","K","","S",""],
};

/** Sixteenth notes — divide-by-4 demo */
export const DIV_BY_4: RhythmPattern = {
  name: "Divide by 4 · 1-e-and-a",
  description: "Each beat now holds four events. Same tempo, four times the activity. The takadimi or sixteenth-note groove.",
  bpm: 90,
  cells: ["K","S","K","S","K","S","K","S","K","S","K","S","K","S","K","S"],
};

/** Swing eighths — two events per beat, the 2nd on the last triplet (two-thirds, ≈67%) */
export const SWING_SHUFFLE: RhythmPattern = {
  name: "Swing Feel",
  description: "Two events per beat, but the second one lands on the third triplet of the beat — two-thirds of the way through instead of halfway. A lean rather than a march. Jazz and blues live here.",
  bpm: 90,
  subdivide: 3,
  cells: ["K","","S","K","","S","K","","S","K","","S"],
};
